package com.castly.admin.ui

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.components.ImportReportView
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.ImportReportDto
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.files.File
import org.w3c.files.FileList
import org.w3c.files.get

private enum class FileUploadStatus { PENDING, UPLOADING, SUCCESS, FAILED }

private data class FileUploadState(
    val file: File,
    val status: FileUploadStatus = FileUploadStatus.PENDING,
    val errorMessage: String? = null
)

internal fun friendlyUploadError(raw: String?): String = when {
    raw == null -> "Upload failed"
    "Failed to fetch" in raw -> "Network error — check your connection and retry"
    "UPLOAD_INTERRUPTED" in raw || "Upload interrupted" in raw -> "Connection lost during upload — please retry"
    "INTERNAL_ERROR" in raw -> "Server error — please retry"
    "Unexpected EOF" in raw -> "Connection lost during upload — please retry"
    else -> raw
}

@Composable
fun EpisodeImportForm(
    apiClient: ApiClient,
    isbn: String,
    onImportComplete: (ImportReportDto) -> Unit = {},
    onCancel: (() -> Unit)? = null
) {
    val colors = AppTheme.colors
    var selectedFiles by remember { mutableStateOf<FileList?>(null) }
    var isUploading by remember { mutableStateOf(false) }
    var isCancelled by remember { mutableStateOf(false) }
    var fileStates by remember { mutableStateOf<List<FileUploadState>>(emptyList()) }
    var currentFileIndex by remember { mutableStateOf(0) }
    var currentFileProgress by remember { mutableStateOf(0f) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var importReport by remember { mutableStateOf<ImportReportDto?>(null) }

    val scope = rememberCoroutineScope()

    // CSS keyframe for spinner animation
    Style {
        "@keyframes castly-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }"
    }

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            property("box-shadow", "0 2px 8px rgba(0,0,0,0.1)")
            themeTransition()
        }
    }) {
        // Title
        H2({
            style {
                fontSize(Typography.xl)
                fontWeight("600")
                color(Color(colors.text))
                marginBottom(Spacing.lg)
            }
        }) {
            Text("Import Episodes from Folder")
        }

        // Description
        P({
            style {
                fontSize(Typography.base)
                color(Color(colors.textMuted))
                marginBottom(Spacing.xxl)
            }
        }) {
            Text("Upload episode MP3 files for this audiobook. Files should be named with the pattern: XXX_$isbn.mp3 where XXX is the episode number (e.g., 001_$isbn.mp3, 002_$isbn.mp3). You can also include a cover image named $isbn.jpg or $isbn.png.")
        }

        // File selection
        if (importReport == null) {
            FormField("Episode Files (MP3 + Cover Image)") {
                Input(InputType.File) {
                    attr("accept", ".mp3,.jpg,.jpeg,.png")
                    attr("multiple", "true")
                    if (isUploading) {
                        disabled()
                    }
                    onChange { event ->
                        val files = event.target.files
                        if (files != null && files.length > 0) {
                            selectedFiles = files
                            errorMessage = null
                            // Build file states list
                            val states = mutableListOf<FileUploadState>()
                            for (i in 0 until files.length) {
                                val file = files[i]
                                if (file != null) {
                                    states.add(FileUploadState(file = file))
                                }
                            }
                            fileStates = states
                        }
                    }
                }
            }

            // Selected files info
            if (fileStates.isNotEmpty() && !isUploading) {
                Div({
                    style {
                        backgroundColor(Color(colors.surfaceHover))
                        padding(Spacing.md)
                        borderRadius(BorderRadius.md)
                        marginTop(Spacing.md)
                        marginBottom(Spacing.lg)
                        maxHeight(200.px)
                        overflowY("auto")
                    }
                }) {
                    var mp3Count = 0
                    var imageCount = 0
                    var totalSize = 0.0

                    for (state in fileStates) {
                        totalSize += state.file.size.toDouble()
                        when {
                            state.file.name.endsWith(".mp3", ignoreCase = true) -> mp3Count++
                            state.file.name.endsWith(".jpg", ignoreCase = true) ||
                            state.file.name.endsWith(".jpeg", ignoreCase = true) ||
                            state.file.name.endsWith(".png", ignoreCase = true) -> imageCount++
                        }
                    }

                    Div({
                        style {
                            fontSize(Typography.base)
                            color(Color(colors.text))
                            fontWeight("500")
                            marginBottom(Spacing.sm)
                        }
                    }) {
                        Text("Selected files: ${fileStates.size}")
                    }

                    Div({
                        style {
                            fontSize(13.px)
                            color(Color(colors.textMuted))
                        }
                    }) {
                        Text("MP3 files: $mp3Count")
                        Br()
                        Text("Cover images: $imageCount")
                        Br()
                        val sizeMb = (totalSize / 1024.0 / 1024.0).toInt()
                        Text("Total size: ${sizeMb} MB")
                    }

                    // Show individual files
                    Div({
                        style {
                            marginTop(Spacing.md)
                            paddingTop(Spacing.md)
                            property("border-top", "1px solid ${colors.border}")
                        }
                    }) {
                        for (state in fileStates) {
                            Div({
                                style {
                                    fontSize(Typography.sm)
                                    color(Color(colors.textMuted))
                                    marginBottom(Spacing.xs)
                                    property("word-break", "break-all")
                                }
                            }) {
                                Text("\u2022 ${state.file.name}")
                            }
                        }
                    }
                }
            }

            // Per-file progress during upload
            if (isUploading && fileStates.isNotEmpty()) {
                val completedCount = fileStates.count { it.status == FileUploadStatus.SUCCESS }
                val failedCount = fileStates.count { it.status == FileUploadStatus.FAILED }
                val totalCount = fileStates.size
                // Smooth progress: completed files + fraction of current file
                val progressPercent = if (totalCount > 0) (((completedCount + failedCount) + currentFileProgress).toDouble() / totalCount * 100).toInt().coerceIn(0, 100) else 0

                Div({
                    style {
                        marginBottom(Spacing.lg)
                    }
                }) {
                    // Overall progress header
                    Div({
                        style {
                            fontSize(Typography.base)
                            color(Color(colors.text))
                            marginBottom(Spacing.sm)
                            fontWeight("500")
                        }
                    }) {
                        Text("Importing episodes... (${completedCount + failedCount} of $totalCount)")
                    }

                    // Determinate progress bar
                    Div({
                        style {
                            width(100.percent)
                            height(8.px)
                            backgroundColor(Color(colors.border))
                            borderRadius(BorderRadius.sm)
                            overflow("hidden")
                        }
                    }) {
                        Div({
                            style {
                                height(100.percent)
                                width(progressPercent.percent)
                                backgroundColor(Color(if (failedCount > 0) "#f59e0b" else colors.primary))
                                property("transition", "width 0.3s ease")
                            }
                        })
                    }

                    // Per-file status list
                    Div({
                        style {
                            marginTop(Spacing.md)
                            maxHeight(200.px)
                            overflowY("auto")
                            backgroundColor(Color(colors.surfaceHover))
                            borderRadius(BorderRadius.md)
                            padding(Spacing.md)
                        }
                    }) {
                        for (state in fileStates) {
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    gap(Spacing.sm)
                                    fontSize(13.px)
                                    marginBottom(4.px)
                                    color(Color(when (state.status) {
                                        FileUploadStatus.SUCCESS -> colors.primary
                                        FileUploadStatus.FAILED -> colors.danger
                                        FileUploadStatus.UPLOADING -> colors.text
                                        FileUploadStatus.PENDING -> colors.textMuted
                                    }))
                                }
                            }) {
                                // Status icon
                                Span({
                                    style {
                                        property("min-width", "16px")
                                        if (state.status == FileUploadStatus.UPLOADING) {
                                            display(DisplayStyle.InlineBlock)
                                            property("animation", "castly-spin 1s linear infinite")
                                        }
                                    }
                                }) {
                                    Text(when (state.status) {
                                        FileUploadStatus.SUCCESS -> "\u2713"
                                        FileUploadStatus.FAILED -> "\u2717"
                                        FileUploadStatus.UPLOADING -> "\u21BB"
                                        FileUploadStatus.PENDING -> "\u2014"
                                    })
                                }
                                Span({
                                    style { property("word-break", "break-all") }
                                }) {
                                    val sizeMB = (state.file.size.toDouble() / 1024 / 1024).asDynamic().toFixed(1)
                                    val filePct = if (state.status == FileUploadStatus.UPLOADING) " ${(currentFileProgress * 100).toInt()}%" else ""
                                    Text("${state.file.name} (${sizeMB} MB)$filePct")
                                }
                                if (state.errorMessage != null) {
                                    Span({
                                        style {
                                            color(Color(colors.danger))
                                            fontSize(11.px)
                                        }
                                    }) {
                                        Text(" - ${state.errorMessage}")
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Error message
            errorMessage?.let { error ->
                Div({
                    style {
                        backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                        border(1.px, LineStyle.Solid, Color(colors.danger))
                        borderRadius(BorderRadius.md)
                        padding(Spacing.md)
                        marginBottom(Spacing.lg)
                    }
                }) {
                    Div({
                        style {
                            fontSize(Typography.base)
                            color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                            fontWeight("500")
                        }
                    }) {
                        Text("Error")
                    }
                    Div({
                        style {
                            fontSize(13.px)
                            color(Color(colors.danger))
                            marginTop(Spacing.xs)
                        }
                    }) {
                        Text(error)
                    }
                }
            }

            // Action buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    gap(Spacing.md)
                    marginTop(Spacing.xxl)
                }
            }) {
                // Cancel button (also used to stop in-progress import)
                if (onCancel != null || isUploading) {
                    Button({
                        onClick {
                            if (isUploading) {
                                isCancelled = true
                            } else {
                                onCancel?.invoke()
                            }
                        }
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            fontSize(Typography.base)
                            fontWeight("500")
                            color(Color(if (isUploading) colors.danger else colors.text))
                            backgroundColor(Color(colors.surface))
                            border(1.px, LineStyle.Solid, Color(if (isUploading) colors.danger else colors.border))
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                            property("transition", "all 0.2s")
                        }
                    }) {
                        Text(if (isUploading) "Cancel Import" else "Cancel")
                    }
                }

                // Retry failed button (shown after upload completes with failures)
                if (!isUploading && fileStates.any { it.status == FileUploadStatus.FAILED }) {
                    Button({
                        onClick {
                            // Reset failed files to pending and re-upload
                            fileStates = fileStates.map { state ->
                                if (state.status == FileUploadStatus.FAILED) state.copy(status = FileUploadStatus.PENDING, errorMessage = null)
                                else state
                            }
                            isCancelled = false
                            isUploading = true
                            errorMessage = null

                            scope.launch {
                                var totalSuccess = fileStates.count { it.status == FileUploadStatus.SUCCESS }
                                var totalFailed = 0
                                val allErrors = mutableListOf<com.castly.shared.dto.ImportErrorDto>()

                                for (i in fileStates.indices) {
                                    if (isCancelled) break
                                    if (fileStates[i].status != FileUploadStatus.PENDING) continue

                                    fileStates = fileStates.toMutableList().also { list ->
                                        list[i] = list[i].copy(status = FileUploadStatus.UPLOADING)
                                    }
                                    currentFileIndex = i
                                    currentFileProgress = 0f

                                    try {
                                        val report = apiClient.importSingleEpisodeFile(isbn, fileStates[i].file) { fraction ->
                                            currentFileProgress = fraction
                                        }
                                        if (report.failedRows > 0) {
                                            totalFailed++
                                            val errorMsg = report.errors.firstOrNull()?.error ?: "Import failed"
                                            allErrors.addAll(report.errors)
                                            fileStates = fileStates.toMutableList().also { list ->
                                                list[i] = list[i].copy(status = FileUploadStatus.FAILED, errorMessage = errorMsg)
                                            }
                                        } else {
                                            totalSuccess += report.successfulRows
                                            fileStates = fileStates.toMutableList().also { list ->
                                                list[i] = list[i].copy(status = FileUploadStatus.SUCCESS)
                                            }
                                        }
                                    } catch (e: Exception) {
                                        totalFailed++
                                        val friendly = friendlyUploadError(e.message)
                                        allErrors.add(com.castly.shared.dto.ImportErrorDto(row = i + 1, filename = fileStates[i].file.name, error = friendly))
                                        fileStates = fileStates.toMutableList().also { list ->
                                            list[i] = list[i].copy(status = FileUploadStatus.FAILED, errorMessage = friendly)
                                        }
                                    }
                                }

                                isUploading = false
                                if (totalFailed == 0) {
                                    val finalReport = ImportReportDto(
                                        importId = 0,
                                        importType = "FOLDER_EPISODE",
                                        status = "SUCCESS",
                                        totalRows = fileStates.size,
                                        successfulRows = totalSuccess,
                                        failedRows = 0,
                                        errors = emptyList(),
                                        createdAt = ""
                                    )
                                    importReport = finalReport
                                    onImportComplete(finalReport)
                                }
                            }
                        }
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            fontSize(Typography.base)
                            fontWeight("500")
                            color(Color("#ffffff"))
                            backgroundColor(Color("#f59e0b"))
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                            property("transition", "all 0.2s")
                        }
                    }) {
                        Text("Retry Failed (${fileStates.count { it.status == FileUploadStatus.FAILED }})")
                    }
                }

                // Import button
                if (!isUploading && fileStates.none { it.status == FileUploadStatus.FAILED }) {
                    Button({
                        if (fileStates.isEmpty() || isUploading) {
                            disabled()
                        }
                        onClick {
                            if (fileStates.isNotEmpty() && !isUploading) {
                                isUploading = true
                                isCancelled = false
                                errorMessage = null
                                // Reset all states to pending
                                fileStates = fileStates.map { it.copy(status = FileUploadStatus.PENDING, errorMessage = null) }

                                scope.launch {
                                    var totalSuccess = 0
                                    var totalFailed = 0
                                    val allErrors = mutableListOf<com.castly.shared.dto.ImportErrorDto>()

                                    for (i in fileStates.indices) {
                                        if (isCancelled) break

                                        fileStates = fileStates.toMutableList().also { list ->
                                            list[i] = list[i].copy(status = FileUploadStatus.UPLOADING)
                                        }
                                        currentFileIndex = i
                                        currentFileProgress = 0f

                                        try {
                                            val report = apiClient.importSingleEpisodeFile(isbn, fileStates[i].file) { fraction ->
                                                currentFileProgress = fraction
                                            }
                                            if (report.failedRows > 0) {
                                                totalFailed++
                                                val errorMsg = report.errors.firstOrNull()?.error ?: "Import failed"
                                                allErrors.addAll(report.errors)
                                                fileStates = fileStates.toMutableList().also { list ->
                                                    list[i] = list[i].copy(status = FileUploadStatus.FAILED, errorMessage = errorMsg)
                                                }
                                            } else {
                                                totalSuccess += report.successfulRows
                                                fileStates = fileStates.toMutableList().also { list ->
                                                    list[i] = list[i].copy(status = FileUploadStatus.SUCCESS)
                                                }
                                            }
                                        } catch (e: Exception) {
                                            totalFailed++
                                            val friendly = friendlyUploadError(e.message)
                                            allErrors.add(com.castly.shared.dto.ImportErrorDto(row = i + 1, filename = fileStates[i].file.name, error = friendly))
                                            fileStates = fileStates.toMutableList().also { list ->
                                                list[i] = list[i].copy(status = FileUploadStatus.FAILED, errorMessage = friendly)
                                            }
                                        }
                                    }

                                    isUploading = false

                                    if (isCancelled) {
                                        errorMessage = "Import cancelled. ${totalSuccess} files imported, ${fileStates.count { it.status == FileUploadStatus.PENDING }} remaining."
                                    } else if (totalFailed == 0) {
                                        val finalReport = ImportReportDto(
                                            importId = 0,
                                            importType = "FOLDER_EPISODE",
                                            status = "SUCCESS",
                                            totalRows = fileStates.size,
                                            successfulRows = totalSuccess,
                                            failedRows = 0,
                                            errors = emptyList(),
                                            createdAt = ""
                                        )
                                        importReport = finalReport
                                        onImportComplete(finalReport)
                                    }
                                    // If there are failures, stay on the form so user can retry
                                }
                            }
                        }
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            fontSize(Typography.base)
                            fontWeight("500")
                            color(Color("#ffffff"))
                            backgroundColor(
                                if (fileStates.isEmpty() || isUploading) Color(colors.textMuted)
                                else Color(colors.primary)
                            )
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor(if (fileStates.isEmpty() || isUploading) "not-allowed" else "pointer")
                            property("transition", "all 0.2s")
                        }
                    }) {
                        Text("Import Episodes")
                    }
                }
            }
        } else {
            // Import complete - show results
            ImportReportView(importReport!!)

            // Close button
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    marginTop(Spacing.xxl)
                }
            }) {
                Button({
                    onClick {
                        importReport = null
                        selectedFiles = null
                        fileStates = emptyList()
                        errorMessage = null
                        onCancel?.invoke()
                    }
                    style {
                        padding(Spacing.sm, Spacing.lg)
                        fontSize(Typography.base)
                        fontWeight("500")
                        color(Color("#ffffff"))
                        backgroundColor(Color(colors.primary))
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        cursor("pointer")
                        property("transition", "all 0.2s")
                    }
                }) {
                    Text("Close")
                }
            }
        }
    }
}
